// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { MedplumClient } from '@medplum/core';
import { CPT, createReference, formatHumanName, getReferenceString } from '@medplum/core';
import type {
  Appointment,
  ChargeItem,
  CodeSystem,
  Encounter,
  Extension,
  Patient,
  Schedule,
  Slot,
} from '@medplum/fhirtypes';
import {
  APPOINTMENT_CHARGE_APPOINTMENT_SYSTEM,
  isBundleCoveredAppointment,
  isSelfPayPatient,
} from '@polarissleep/shared';
import { parseAppointmentTypeCodeSystem } from './appointment-type-parsing';
import type { AppointmentTypeDefinition } from './appointment-types';
import { APPOINTMENT_TYPE_SYSTEM } from './appointment-types';
import { ensurePractitionerOnCareTeam } from './care-team';
import { resolveChargeForAppointmentType } from './pre-encounter-charge';
import type { BookAppointmentResult } from './types';

export interface BookAppointmentParams {
  patient: Patient;
  slotId: string;
  appointmentTypeCode: string;
  providerId: string;
  start: string;
  end: string;
  appointmentType?: AppointmentTypeDefinition;
  insuranceDeferredBillingEnabled?: boolean;
  /** Skip the duplicate-type guard for clinician-initiated booking. */
  allowDuplicateType?: boolean;
  decorate?: <T extends { resourceType: string }>(resource: T) => T;
}

/**
 * Book an appointment.
 *
 * For non-chargeable types creates `Appointment(booked)` + `Encounter(planned)`.
 * For chargeable types (CPT in PRE_ENCOUNTER_CHARGEABLE_CPTS) creates
 * `Appointment(booked)` + `Encounter(planned)` + `ChargeItem(planned)`.
 *
 * Rollback on partial failure: releases slot and deletes partial resources on error.
 */
export async function bookAppointment(
  medplum: MedplumClient,
  params: BookAppointmentParams
): Promise<BookAppointmentResult> {
  const decorate = params.decorate ?? (<T>(r: T) => r);
  const { patient, slotId, appointmentTypeCode, providerId, start, end } = params;

  const slot = await medplum.readResource('Slot', slotId);
  if (slot.status !== 'free') {
    throw new Error('This time slot is no longer available.');
  }
  if (slot.start && new Date(slot.start).getTime() <= Date.now()) {
    throw new Error('This time slot has already passed. Please select a future time.');
  }

  if (patient.id && !params.allowDuplicateType) {
    const existingAppointments = await medplum.searchResources('Appointment', [
      ['patient', `Patient/${patient.id}`],
      ['_count', '100'],
    ]);
    const hasDuplicate = existingAppointments.some(
      (appt) =>
        (appt.status === 'booked' || appt.status === 'pending') &&
        appt.appointmentType?.coding?.[0]?.code === appointmentTypeCode
    );
    if (hasDuplicate) {
      throw new Error(
        'You already have an appointment of this type. Your care team will advance you to the next stage.'
      );
    }
  }

  let appointmentType = params.appointmentType;
  if (!appointmentType) {
    const codeSystem = await medplum.searchOne('CodeSystem', { url: APPOINTMENT_TYPE_SYSTEM });
    if (!codeSystem) {
      throw new Error('Appointment type configuration not found.');
    }
    const types = parseAppointmentTypeCodeSystem((codeSystem as CodeSystem).concept ?? []);
    appointmentType = types.find((t) => t.coding.code === appointmentTypeCode);
    if (!appointmentType) {
      throw new Error(`Unknown appointment type: ${appointmentTypeCode}`);
    }
  }

  const suppressForBundle = await isBundleCoveredAppointment(medplum, patient, appointmentTypeCode, {
    appointmentStart: start,
  });
  const deferInsuranceBilling = params.insuranceDeferredBillingEnabled === true && !isSelfPayPatient(patient);
  const chargeSpec =
    suppressForBundle || deferInsuranceBilling
      ? null
      : await resolveChargeForAppointmentType(medplum, appointmentType, patient, start);

  const claim = await claimSlotWithFallback(medplum, slot, slotId, appointmentTypeCode, start, providerId);
  const claimedSlot = claim.claimedSlot;
  const actualProviderId = claim.providerId;

  let createdAppointment: Appointment | undefined;
  let createdEncounter: Encounter | undefined;
  let createdChargeItem: ChargeItem | undefined;

  try {
    const practitioner = await medplum.readResource('Practitioner', actualProviderId);

    createdAppointment = await medplum.createResource(
      decorate({
        resourceType: 'Appointment',
        status: 'booked',
        start,
        end,
        appointmentType: { coding: [appointmentType.coding], text: appointmentType.display },
        slot: [createReference(claimedSlot)],
        participant: [
          {
            actor: { reference: getReferenceString(patient), display: formatHumanName(patient.name?.[0] ?? {}) },
            status: 'accepted',
          },
          {
            actor: {
              reference: getReferenceString(practitioner),
              display: formatHumanName(practitioner.name?.[0] ?? {}),
            },
            status: 'accepted',
          },
        ],
      } as Appointment)
    );

    createdEncounter = await medplum.createResource(
      decorate({
        resourceType: 'Encounter',
        status: 'planned',
        class: {
          system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
          code: 'VR',
          display: 'virtual',
        },
        type: [{ coding: [appointmentType.coding], text: appointmentType.display }],
        subject: createReference(patient),
        appointment: [createReference(createdAppointment)],
        participant: [{ individual: { reference: getReferenceString(practitioner) } }],
      } as Encounter)
    );

    if (chargeSpec) {
      const chargeDraft: ChargeItem = {
        resourceType: 'ChargeItem',
        status: 'planned',
        subject: createReference(patient),
        context: createReference(createdEncounter),
        service: [createReference(createdAppointment)] as unknown as ChargeItem['service'],
        code: {
          coding: [{ system: CPT, code: chargeSpec.cptCode, display: appointmentType.display }],
        },
        definitionCanonical: [chargeSpec.chargeItemDefinitionUrl],
        occurrenceDateTime: start,
        identifier: [{ system: APPOINTMENT_CHARGE_APPOINTMENT_SYSTEM, value: createdAppointment.id ?? '' }],
        priceOverride: { value: chargeSpec.amountUsd, currency: 'USD' },
        quantity: { value: 1 },
        ...(appointmentType.billingModifier
          ? {
              extension: [
                {
                  url: 'http://hl7.org/fhir/StructureDefinition/chargeitem-modifier',
                  valueCodeableConcept: {
                    coding: [{ system: CPT, code: appointmentType.billingModifier }],
                  },
                },
              ],
            }
          : {}),
      };
      createdChargeItem = await medplum.createResource(decorate(chargeDraft));
    }

    await ensurePractitionerOnCareTeam(medplum, patient, practitioner, { decorate });

    // Best-effort overlap blocking. Failure to mark peer slots unavailable must
    // not roll back the successfully created appointment/encounter/charge.
    try {
      await blockOverlappingSlots(medplum, claimedSlot);
    } catch (err) {
      console.warn('bookAppointment: blockOverlappingSlots failed (non-fatal)', err);
    }

    return {
      appointmentId: createdAppointment.id as string,
      encounterId: createdEncounter.id as string,
      requiresPayment: !!chargeSpec,
    };
  } catch (err) {
    if (createdChargeItem?.id) {
      try {
        await medplum.deleteResource('ChargeItem', createdChargeItem.id);
      } catch {
        /* best-effort */
      }
    }
    if (createdEncounter?.id) {
      try {
        await medplum.deleteResource('Encounter', createdEncounter.id);
      } catch {
        /* best-effort */
      }
    }
    if (createdAppointment?.id) {
      try {
        await medplum.deleteResource('Appointment', createdAppointment.id);
      } catch {
        /* best-effort */
      }
    }
    try {
      await medplum.updateResource<Slot>({ ...claimedSlot, status: 'free' });
    } catch {
      /* best-effort */
    }
    throw err;
  }
}

async function claimSlotWithFallback(
  medplum: MedplumClient,
  primarySlot: Slot,
  primarySlotId: string,
  appointmentTypeCode: string,
  start: string,
  defaultProviderId: string
): Promise<{ claimedSlot: Slot; providerId: string }> {
  try {
    const claimed = await medplum.updateResource<Slot>({ ...primarySlot, status: 'busy' });
    return { claimedSlot: claimed, providerId: defaultProviderId };
  } catch {
    /* primary taken; try fallback */
  }

  const fallbackSlots = await medplum.searchResources('Slot', [
    ['status', 'free'],
    ['service-type', `${APPOINTMENT_TYPE_SYSTEM}|${appointmentTypeCode}`],
    ['start', `eq${start}`],
    ['_count', '10'],
  ]);

  for (const altSlot of fallbackSlots) {
    if (!altSlot.id || altSlot.id === primarySlotId || !altSlot.schedule?.reference) {
      continue;
    }

    // Resolve provider BEFORE claiming — skip slots with no Practitioner actor
    let altProviderId: string | undefined;
    try {
      const schedule = (await medplum.readReference(altSlot.schedule)) as Schedule;
      const actorRef = schedule.actor?.[0]?.reference;
      if (actorRef?.startsWith('Practitioner/')) {
        altProviderId = actorRef.replace('Practitioner/', '');
      }
    } catch {
      /* can't resolve schedule */
    }

    if (!altProviderId) {
      continue;
    }

    try {
      const claimed = await medplum.updateResource<Slot>({ ...altSlot, status: 'busy' });
      return { claimedSlot: claimed, providerId: altProviderId };
    } catch {
      /* try next */
    }
  }

  throw new Error('This time slot is no longer available. Please select another time.');
}

/** Extension URL used to track which booked slot caused an overlap block */
export const BLOCKED_BY_SLOT_EXTENSION_URL = 'http://polarissleep.com/fhir/StructureDefinition/blocked-by-slot';

/**
 * Blocks all free slots on the same schedule that overlap with the booked slot's time range.
 * Overlapping slots are marked as 'busy-unavailable' with an extension referencing the booked slot
 * so they can be restored if the booking is later cancelled.
 *
 * @param medplum - The Medplum client
 * @param bookedSlot - The slot that was just booked (must have id, schedule, start, end)
 * @returns Number of slots that were blocked
 */
export async function blockOverlappingSlots(medplum: MedplumClient, bookedSlot: Slot): Promise<number> {
  if (!bookedSlot.id || !bookedSlot.schedule?.reference || !bookedSlot.start || !bookedSlot.end) {
    return 0;
  }

  // Find free slots on the same schedule that overlap with the booked slot's time range
  // Overlap condition: slotStart < bookedEnd AND slotEnd > bookedStart
  const overlappingSlots = await medplum.searchResources('Slot', [
    ['schedule', bookedSlot.schedule.reference],
    ['status', 'free'],
    ['start', `lt${bookedSlot.end}`],
    ['_count', '1000'],
  ]);

  const bookedStart = new Date(bookedSlot.start).getTime();
  // const bookedEnd = new Date(bookedSlot.end).getTime();

  let blockedCount = 0;

  for (const slot of overlappingSlots) {
    if (!slot.id || slot.id === bookedSlot.id || !slot.start || !slot.end) {
      continue;
    }

    const slotEnd = new Date(slot.end).getTime();

    // Check overlap: slot ends after booked starts (and we already filtered start < bookedEnd)
    if (slotEnd <= bookedStart) {
      continue;
    }

    // Mark as busy-unavailable with blocked-by reference
    const existingExtensions = slot.extension ?? [];
    await medplum.updateResource<Slot>({
      ...slot,
      status: 'busy-unavailable',
      extension: [
        ...existingExtensions,
        {
          url: BLOCKED_BY_SLOT_EXTENSION_URL,
          valueReference: { reference: `Slot/${bookedSlot.id}` },
        },
      ],
    });
    blockedCount++;
  }

  return blockedCount;
}

/**
 * Restores slots that were blocked by a specific booked slot.
 * Only restores a slot if it doesn't overlap with any other busy (booked) slot on the same schedule.
 *
 * @param medplum - The Medplum client
 * @param freedSlot - The slot that was just freed (cancelled booking)
 * @returns Number of slots that were restored to free
 */
export async function restoreOverlappingSlots(medplum: MedplumClient, freedSlot: Slot): Promise<number> {
  if (!freedSlot.id || !freedSlot.schedule?.reference) {
    return 0;
  }

  // Find busy-unavailable slots that have the blocked-by extension referencing this slot
  const blockedSlots = await medplum.searchResources('Slot', [
    ['schedule', freedSlot.schedule.reference],
    ['status', 'busy-unavailable'],
    ['_count', '1000'],
  ]);

  const slotsBlockedByThis = blockedSlots.filter((slot) =>
    slot.extension?.some(
      (ext: Extension) =>
        ext.url === BLOCKED_BY_SLOT_EXTENSION_URL && ext.valueReference?.reference === `Slot/${freedSlot.id}`
    )
  );

  if (slotsBlockedByThis.length === 0) {
    return 0;
  }

  // Find all currently busy (booked) slots on the same schedule to check for other conflicts
  const busySlots = await medplum.searchResources('Slot', [
    ['schedule', freedSlot.schedule.reference],
    ['status', 'busy'],
    ['_count', '1000'],
  ]);

  let restoredCount = 0;

  for (const slot of slotsBlockedByThis) {
    if (!slot.id || !slot.start || !slot.end) {
      continue;
    }

    const slotStart = new Date(slot.start).getTime();
    const slotEnd = new Date(slot.end).getTime();

    // Check if this slot overlaps with any other busy (booked) slot
    const hasOtherConflict = busySlots.some((busy) => {
      if (!busy.start || !busy.end || busy.id === freedSlot.id) {
        return false;
      }
      const busyStart = new Date(busy.start).getTime();
      const busyEnd = new Date(busy.end).getTime();
      return slotStart < busyEnd && slotEnd > busyStart;
    });

    // Both update paths use `...slot` which carries meta.versionId from the earlier read.
    // If a concurrent cancellation already modified this slot, the update will fail with
    // a version conflict. This is safe to skip — the concurrent operation handled it.
    try {
      if (hasOtherConflict) {
        // Remove this specific blocked-by extension but keep the slot busy-unavailable
        const updatedExtensions = (slot.extension ?? []).filter(
          (ext: Extension) =>
            !(ext.url === BLOCKED_BY_SLOT_EXTENSION_URL && ext.valueReference?.reference === `Slot/${freedSlot.id}`)
        );
        await medplum.updateResource<Slot>({
          ...slot,
          extension: updatedExtensions.length > 0 ? updatedExtensions : undefined,
        });
        continue;
      }

      // No other conflicts — restore to free and remove extension
      const updatedExtensions = (slot.extension ?? []).filter(
        (ext: Extension) => ext.url !== BLOCKED_BY_SLOT_EXTENSION_URL
      );
      await medplum.updateResource<Slot>({
        ...slot,
        status: 'free',
        extension: updatedExtensions.length > 0 ? updatedExtensions : undefined,
      });
      restoredCount++;
    } catch {
      // Version conflict — a concurrent cancellation already modified this slot.
      // The slot's state has been handled by the other operation, safe to skip.
    }
  }

  return restoredCount;
}
